import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Checkbox, notification } from 'antd';
import Cookies from 'js-cookie';
import Head from '../../components/Head';
import './auth.css';

function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatedPassword, setRepeatedPassword] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const signedIn = Cookies.get('token') ? true : false;

  useEffect(() => {
    if (signedIn) {
      if (!(Cookies.get('subuser') === 'true')) navigate('/dashboard');
      else navigate('/dashboard/chat');
    }
  }, [signedIn, navigate]);

  useEffect(() => {
    const initGoogle = () => {
      if (!window.google?.accounts?.id) return false;
      window.google.accounts.id.initialize({
        client_id: '493483959965-rspkljm6ergq7bth5t2g4aac1psd7cvj.apps.googleusercontent.com',
        callback: handleCredentialResponse,
      });
      window.google.accounts.id.renderButton(document.getElementById('divGbox'), {
        text: 'continue_with',
        shape: 'rectangular',
        width: '220px',
      });
      return true;
    };
    if (!initGoogle()) {
      const interval = setInterval(() => {
        if (initGoogle()) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const handleCredentialResponse = (response) => {
    handleGoogleSignIn(response.credential);
  };

  const handleGoogleSignIn = async (credential) => {
    const affiliate_code = location.state?.code || await localStorage.getItem('affiliateCode');
    try {
      const response = await fetch(global.SERVIP + '/auth/google-sign-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential, affiliate_code }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (data.ok) {
        Cookies.set('token', data.token, { expires: 15, path: '/' });
        Cookies.set('subuser', data.subuser ? true : false, { expires: 15, path: '/' });
        navigate(data.subuser ? '/dashboard' : '/dashboard/onboarding');
      } else {
        notification.error({ message: data.message });
        window.google.accounts.id.prompt();
      }
    } catch {
      console.error('Google sign-in failed');
    }
  };

  const checkValues = () => {
    if (password !== repeatedPassword) {
      notification.error({ message: 'Passwords do not match.' });
      return false;
    }
    if (password.length < 8) {
      notification.error({ message: 'Password must be at least 8 characters long.' });
      return false;
    }
    if (!termsAccepted) {
      notification.error({ message: 'You must accept the Terms of Service.' });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!checkValues()) return;
    const affiliate_code = location.state?.code || await localStorage.getItem('affiliateCode');

    try {
      const response = await fetch(global.SERVIP + '/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, affiliate_code }),
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (data.ok) {
        setRegistrationComplete(true);
        setTimeout(() => setRegistrationComplete(false), 20000);
      } else {
        setRegistrationError(data.message || 'Error creating an account');
        setTimeout(() => setRegistrationError(''), 5000);
      }
    } catch (error) {
      notification.error({ message: error.message });
    }
  };

  return (
    <div className="auth-page">
      <Head title="Sign Up" />

      {/* Left panel */}
      <div className="auth-left">
        <Link to="/" className="auth-left-logo">
          <img src="/assets/img/logo/logo2.png" alt="TextLink" />
          <span>TextLink</span>
        </Link>

        <div className="auth-left-content">
          <h2 className="auth-left-tagline">
            Skip 10DLC.
            <span>Use your own SIM.</span>
          </h2>
          <p className="auth-left-desc">
            Get started with 300 free messages. No credit card needed. Connect your Android phone and start sending SMS in under 5 minutes.
          </p>
        </div>

        <div className="auth-left-stats">
          <div className="auth-stat">
            <div className="auth-stat-value">$0.001</div>
            <div className="auth-stat-label">Per SMS</div>
          </div>
          <div className="auth-stat">
            <div className="auth-stat-value">300</div>
            <div className="auth-stat-label">Free messages</div>
          </div>
          <div className="auth-stat">
            <div className="auth-stat-value">5 min</div>
            <div className="auth-stat-label">Setup time</div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="auth-right">
        <div className="auth-form-container">
          <h1 className="auth-heading">Create your account</h1>
          <p className="auth-subheading">Start sending SMS in under 5 minutes</p>

          {registrationError && (
            <div className="auth-alert auth-alert--error">{registrationError}</div>
          )}
          {registrationComplete && (
            <div className="auth-alert auth-alert--success">
              Confirmation email sent to <strong>{email}</strong>. Check your inbox to complete registration.
            </div>
          )}

          <div className="auth-google" id="divGbox"></div>
          <div className="auth-divider"><span>or</span></div>

          <Form name="register" onFinish={handleSubmit} layout="vertical">
            <Form.Item
              name="email"
              label="Email Address"
              rules={[{ required: true, message: 'Please enter your email', type: 'email' }]}
            >
              <Input
                autoCapitalize="none"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter a password' }]}
            >
              <Input.Password onChange={(e) => setPassword(e.target.value)} placeholder="Password" />
            </Form.Item>

            <Form.Item
              name="password2"
              label="Confirm Password"
              rules={[{ required: true, message: 'Please confirm your password' }]}
            >
              <Input.Password
                onChange={(e) => setRepeatedPassword(e.target.value)}
                placeholder="Repeat password"
              />
            </Form.Item>

            <Form.Item>
              <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}>
                I agree to the{' '}
                <a href="https://textlinksms.com/tos" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button
                htmlType="submit"
                type="primary"
                size="large"
                style={{ width: '100%' }}
                disabled={!termsAccepted}
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>

          <p className="auth-footer">
            Already have an account? <a href="/signin">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
